import dynamic from 'next/dynamic';
import { useFeatureIsOn } from '@growthbook/growthbook-react';
import { isNullish } from '@utils/objects';
import { ErrorBoundary } from '@components/utils';
import { AboutValueSkeleton, RecommendationCarouselSkeleton } from '@components/common';
import { PRODUCT_TYPES, useTranslation } from '@utils/index';
import { NormalizedVariant } from '@ts/index';
import { LicensingDataProps } from '../LicensingInfoArea';
import ProductView from '../ProductView';

const AboutValueProp = dynamic(() => import('@components').then(mod => mod.AboutValueProp));
const LicensingInfoArea = dynamic(() => import('@components').then(mod => mod.LicensingInfoArea));
const RecommendationCarousel = dynamic(() => import('@components').then(mod => mod.RecommendationCarousel));

const getTranslatedTexts = translator => {
	return {
		similarToProduct: (productName: string) => translator('similar-to-product', { productName }),
		whyLoveTopFrames: translator('why-love-top-frames'),
	};
};
type TopPDPContentProps = {
	isStickySidebar: boolean;
	licensingData: LicensingDataProps;
	variant?: NormalizedVariant;
	isLoading?: boolean;
	frameShape?: string;
	name?: string;
};
export function TopPDPContent({ isStickySidebar, licensingData, isLoading, variant, frameShape, name }: TopPDPContentProps) {
	const { translator } = useTranslation();
	const translations = getTranslatedTexts(translator);
	const isSoldOutTreatment = useFeatureIsOn('is-sold-out-treatment');

	const isSoldOut = !variant?.availableForSale && isSoldOutTreatment;

	const TopFrameDetails = () => {
		return (
			<ProductView.Details>
				{licensingData && !isNullish(licensingData) && <LicensingInfoArea {...licensingData} />}
				<ErrorBoundary skeleton={<AboutValueSkeleton />}>
					<AboutValueProp
						heading={translations.whyLoveTopFrames}
						type={PRODUCT_TYPES.TOP_FRAME}
						withOutMinWidth={isStickySidebar}
					/>
				</ErrorBoundary>
			</ProductView.Details>
		);
	};

	const Recommendations = () => {
		return (
			<ErrorBoundary skeleton={<RecommendationCarouselSkeleton />}>
				<RecommendationCarousel
					variant={!isLoading ? variant : null}
					selectedFrame={frameShape}
					headingText={translations.similarToProduct(name)}
					dataTags={{
						button: { 'data-add-to-cart-from-recommendation': true },
						zoom: {},
						favorite: { 'data-add-favorite-from-recommendation': true },
					}}
					withPadding={isStickySidebar}
				/>
			</ErrorBoundary>
		);
	};

	const components = {
		TopFrameDetails: <TopFrameDetails />,
		Recommendations: <Recommendations />,
	};

	const layoutStategy = {
		soldOut: ['Recommendations', 'TopFrameDetails'],
		default: ['TopFrameDetails', 'Recommendations'],
	};

	const content = layoutStategy[isSoldOut ? 'soldOut' : 'default'].map(component => components[component]);

	return isStickySidebar ? content : <ProductView.Recommendations>{content}</ProductView.Recommendations>;
}
